import BackgroundGeolocation, {
    State,
    Location,
    Subscription, HttpConfig
} from "@transistorsoft/capacitor-background-geolocation";
import { Device } from "@capacitor/device";
import { Network } from '@capacitor/network';
import { apiFetchExternal } from "@/services/ApiClient.ts";
import {StorageService} from "@/services/storageService.ts";

interface  UserConfig {
    id: string,
    shift: string,
    env: string
}

export interface DeviceStatus {
    isRunning: boolean,
    permissionLevel: number,
    hasPermission: boolean,
    isLocationServiceEnabled: boolean,
    hasInternetConnection: boolean,
    internetConnectionType: string,
    currentLocation: Location,
    lastLocationHttpSuccess: boolean
}

export class GeoLocationService {
    private static instance: GeoLocationService;
    private isInitialized: boolean = false;
    private subscriptions: Subscription[] = [];
    private deviceModel: string = '';
    private deviceUUID: string = '';
    private locationServerUrl: string = '';
    private lastLocationHttpSuccess: boolean = false;

    // Private constructor to prevent direct instantiation
    private constructor() {
        Device.getInfo().then(info => this.deviceModel = info.model)
        Device.getId().then(deviceId => this.deviceUUID = deviceId.identifier)
    }

    /**
     * Returns the singleton instance of the service
     */
    public static getInstance(): GeoLocationService {
        if (!GeoLocationService.instance) {
            GeoLocationService.instance = new GeoLocationService();
        }
        return GeoLocationService.instance;
    }

    /**
     * Initializes the plugin once.
     * Configure your backend URL and tracking settings here.
     */
    public async initialize(): Promise<void> {
        if (this.isInitialized) {
            return
        }

        this.subscriptions.push(
            BackgroundGeolocation.onLocation((location) => {
                console.log('[location] - ', location);
            }),
            BackgroundGeolocation.onHttp((response) => {
                this.lastLocationHttpSuccess = response.success
            }),
            BackgroundGeolocation.onMotionChange((event) => {
                console.log('[motionChange] - ', event.isMoving, event.location);
            })
        );

        const geoPluginHttpConfig = await StorageService.Get<HttpConfig>("GEO_PLUGIN_HTTP_CONFIG")
        const state = await BackgroundGeolocation.ready({
            geolocation: {
                allowIdenticalLocations: true,
                geofenceProximityRadius: 10,
                geofenceModeHighAccuracy: true,
                distanceFilter: 10
            },
            logger: {
                debug: false, // silence the beeps
                logLevel: 4,
                logMaxDays: 3,
            },
            http: geoPluginHttpConfig,
            app: {
                startOnBoot: true,
                stopOnTerminate: false,
                backgroundPermissionRationale: {
                    title: "Allow {applicationName} to access to this device's location in the background?",
                    message: "In order to track your activity in the background, please enable {backgroundPermissionOptionLabel} location permission",
                    positiveAction: "Change to {backgroundPermissionOptionLabel}",
                    negativeAction: "Cancel"
                }
            },
            persistence: {
                locationTemplate: '{"latitude":<%= latitude %>,"longitude":<%= longitude %>,"accuracy":<%= accuracy %>,"timestamp":"<%= timestamp %>","isMoving":<%= is_moving %>}',
            },
        });
        this.isInitialized = true;
        StorageService.Set("SHOULD_RUN_GEO_PLUGIN_ON_BEFORE_MOUNT", true)
        return;
    }

    /**
     * Starts tracking
     */
    public async start(userConfig: UserConfig): Promise<void> {
        this.locationServerUrl = await StorageService.Get("LOCATION_SERVICE_URL")

        const http: HttpConfig = {
            // todo remove when delivery exist in head url: `http://192.168.1.57:8051/add`,
            url: `${this.locationServerUrl}/add`,
                params: {
                    device: this.deviceModel,
                    token: userConfig.id,
                    env: userConfig.env,
                    shiftId: userConfig.shift,
                    useLocationChannel: true,
            }
        }

        StorageService.Set("GEO_PLUGIN_HTTP_CONFIG", http)

        BackgroundGeolocation.setConfig({ http }).then(async state => {
            if (!state.enabled) {
                await BackgroundGeolocation.start();
                console.log('Tracking started');
            }
        })
    }

    /**
     * Stops tracking
     */
    public async stop(): Promise<void> {
        await BackgroundGeolocation.stop();
        StorageService.Set("SHOULD_RUN_GEO_PLUGIN_ON_BEFORE_MOUNT", false)
        console.log('Tracking stopped');
    }
    
    public async getDeviceStatus(): Promise<DeviceStatus> {
        const state = await BackgroundGeolocation.getState()
        const provider = await BackgroundGeolocation.getProviderState();
        const status = await Network.getStatus();
        const location = await BackgroundGeolocation.getCurrentPosition({
            timeout: 30,          // 30 second timeout to fetch location
            maximumAge: 30000,     // Accept the last-known-location if not older than 30 seconds.
            desiredAccuracy: 10,  // Try to fetch a location with an accuracy of `10` meters.
            samples: 3            // How many location samples to attempt.
        });
        return {
            isRunning: state.enabled,
            permissionLevel: provider.status,
            hasPermission: provider.status === BackgroundGeolocation.AuthorizationStatus.Always,
            isLocationServiceEnabled: provider.gps,
            hasInternetConnection: status.connected,
            internetConnectionType: status.connectionType,
            currentLocation: location,
            lastLocationHttpSuccess: this.lastLocationHttpSuccess
        }
    }

    public async sendLocationLogs(data: any): Promise<void> {
        // if (self.backgroundLocationCaller) {
        if (true) {
            const Logger = BackgroundGeolocation.logger;
            const log = await Logger.getLog();
            const locations = await BackgroundGeolocation.getLocations(); // why not await BackgroundGeolocation.sync() ?
            const url = `${this.locationServerUrl}/log`;

            const payload = {
                'uuid': this.deviceUUID,
                'data': JSON.stringify({
                    locations,
                    log,
                    ...await this.getDeviceStatus(),
                    ...data
                })
            }

            apiFetchExternal(url, payload)
                .then(() => console.log('success apiFetchExternal'))
                .catch(e => console.error('failed to send location logs', e));
        }
    }

    public async sendApplicationLogs(data: []): Promise<void> {
        // if (self.backgroundLocationCaller) {
        if (true) {
            const url = `${this.locationServerUrl}/log`;

            const payload = {
                'uuid': this.deviceUUID,
                'data': JSON.stringify({
                    ...data
                })
            };

            apiFetchExternal(url, payload)
                .then(() => console.log('success apiFetchExternal'))
                .catch(e => console.error('failed to send application logs', e));
        }
    }

    /**
     * Cleanup listeners (useful for HMR or app destruction)
     */
    public destroy() {
        this.subscriptions.forEach(sub => sub.remove());
        this.subscriptions = [];
    }
}